import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "react-emotion";
import { isDeprecated } from "upn-modules-dev-utils";
import { css } from "emotion";

const backgroundColours = {
  default: "rgb(229, 229, 229)",
  primary: "rgb(0, 113, 206)",
  success: "rgb(92, 184, 92)",
  danger: "rgb(209, 50, 57)"
};

const borderColours = {
  default: "rgb(206, 206, 206)",
  primary: "rgb(0, 102, 185)",
  success: "rgb(83, 166, 83)",
  danger: "rgb(188, 45, 51)"
};

const textColours = {
  default: "rgba(0, 0, 0, 0.95)",
  primary: "rgba(255, 255, 255, 0.95)",
  danger: "rgba(255, 255, 255, 0.95)",
  success: "rgba(255, 255, 255, 0.95)"
};

const baseStyles = css`
  background-image: initial;
  border-width: 1px;
  border-style: solid;
  border-radius: 3px;
  font-weight: initial;
  margin: 0.4em;
  text-align: center;
  box-sizing: border-box;
  box-shadow: initial;
  display: inline-block;
  line-height: normal;
  cursor: pointer;

  &:active {
    box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
  }

  &:focus {
    box-shadow: 0 3px 5px rgba(0, 0, 0, 0.125);
  }
`;

const baseThemedStyles = theme => css`
  color: ${textColours[theme]};
  background-color: ${backgroundColours[theme]};
  border-color: ${borderColours[theme]};
`;

/*
Precalculated from this PostCSS:
(default, substitute with primary for primary, danger for danger, etc)

    background-color: color(var(--upn-style-button-default-color) tint(var(--upn-style-button-disabled-tint)));
    color: color(var(--upn-style-button-default-text-color) tint(var(--upn-style-button-disabled-tint)));
    border-color: color(var(--upn-style-button-default-color) tint(var(--upn-style-button-disabled-tint)));
*/

const disabledStyles = {
  default: css`
    background-color: rgb(239, 239, 239);
    color: rgba(102, 102, 102, 0.95);
    border-color: rgb(239, 239, 239);
  `,
  primary: css`
    background-color: rgb(102, 170, 226);
    border-color: rgb(102, 170, 226);
    color: rgba(255, 255, 255, 0.95);
  `,
  danger: css`
    background-color: rgb(227, 132, 136);
    border-color: rgb(227, 132, 136);
    color: rgba(255, 255, 255, 0.95);
  `,
  success: css`
    background-color: rgb(157, 212, 157);
    border-color: rgb(157, 212, 157);
    color: rgba(255, 255, 255, 0.95);
  `
};

/*
Precalculated from this PostCSS:

    color(var(--upn-style-button-primary-color) tint(var(--upn-style-button-hover-tint)))
*/

const hoverStyles = {
  default: css`
    background-color: rgb(234, 234, 234);
  `,
  primary: css`
    background-color: rgb(51, 141, 216);
  `,
  danger: css`
    background-color: rgb(218, 91, 97);
  `,
  success: css`
    background-color: rgb(125, 198, 125);
  `
};

const sizeStyles = {
  small: css`
    font-size: 0.7em;
    padding: 0.2em 0.3em;
  `,
  normal: css`
    font-size: 0.75em;
    padding: 0.4em 0.5em;
  `,
  large: css`
    font-size: 1em;
    padding: 0.4em 0.5em;
  `
};

const StyledButton = styled("button")`
  ${baseStyles} ${({ btheme }) => baseThemedStyles(btheme)};
  ${({ size }) => sizeStyles[size]};

  &:hover {
    ${({ btheme }) => hoverStyles[btheme]};
  }

  &:disabled {
    ${({ btheme }) => disabledStyles[btheme]};
  }
`;

export default class Button extends Component {
  static propTypes = {
    text: isDeprecated(
      PropTypes.string,
      "text",
      "Use 'children' prop now instead."
    ),
    size: PropTypes.oneOf(["large", "normal", "small"]),
    theme: PropTypes.oneOf(["default", "primary", "success", "danger"]),
    className: PropTypes.string,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.string
    ]),
    onClick: PropTypes.func,
    disabled: PropTypes.bool
  };

  static defaultProps = {
    size: "normal",
    theme: "default",
    disabled: false
  };

  render = () => {
    let classNames = [
      "upn-component-button",
      `upn-component-button-${this.props.theme}`,
      `upn-component-button--${this.props.size}`
    ];
    if (this.props.className != null) {
      classNames.push(this.props.className);
    }
    return (
      <StyledButton
        className={classNames.join(" ")}
        onClick={e => {
          e.preventDefault(); //stop asp postback
          this.props.onClick(e);
        }}
        disabled={this.props.disabled}
        size={this.props.size}
        btheme={this.props.theme}
      >
        {this.props.text}
        {this.props.children}
      </StyledButton>
    );
  };
}
